import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, Search, X } from "lucide-react";
import { scrollToId, EASE } from "@/lib/site";
import ShabbatBar from "@/components/ShabbatBar";

const GROUPS = [
  {
    label: "About",
    items: [
      { label: "Our Story", href: "/story" },
      { label: "Our Leadership", href: "/story#leadership" },
      { label: "Our Rabbi", href: "/rabbi" },
      { label: "Memories & Photos", href: "/memories" },
      { label: "News", href: "/news" },
      { label: "Bulletins", href: "/bulletins" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
  {
    label: "Worship",
    items: [
      { label: "Services & Times", href: "#worship" },
      { label: "High Holy Days", href: "/hhd" },
      { label: "Watch Live", href: "/live" },
      { label: "Life Cycle", href: "/contact#lifecycle" },
    ],
  },
  {
    label: "Community",
    items: [
      { label: "Groups & Circles", href: "/groups" },
      { label: "Kids & Family", href: "/kids" },
      { label: "Young Professionals", href: "/groups#yjpn" },
      { label: "Adult Learning", href: "/learning" },
      { label: "The Schmooze", href: "/schmooze" },
    ],
  },
  {
    label: "Events",
    items: [
      { label: "Upcoming Events", href: "/upcoming" },
      { label: "Full Calendar", href: "/calendar" },
      { label: "Find an Event", href: "/calendar#find-event" },
    ],
  },
  {
    label: "Membership",
    items: [
      { label: "Membership & Levels", href: "#join" },
      { label: "Join Beth El", href: "https://templebethelwpb.shulcloud.com/form/2026-2027-temple-beth-el-membership-form" },
      { label: "Member Login", href: "/members" },
      { label: "My Account", href: "https://templebethelwpb.shulcloud.com/member/" },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Navbar({ sticky = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href, sameTab = false) => {
    setOpen(false);
    setActiveGroup(null);
    if (href.startsWith("http")) {
      if (sameTab) window.location.assign(href);
      else window.open(href, "_blank", "noopener");
      return;
    }
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(href), 500);
      return;
    }
    scrollToId(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`${sticky ? "sticky" : "fixed"} top-0 left-0 right-0 z-50 print:hidden transition-[background-color,border-color,backdrop-filter] duration-500 ${
        sticky || scrolled || open || activeGroup ? "bg-ink/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <ShabbatBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 2xl:px-10 h-16 md:h-20 flex items-center gap-4 2xl:gap-8">
        <button
          data-testid="nav-logo"
          onClick={() => {
            setActiveGroup(null);
            if (location.pathname !== "/") navigate("/");
            else window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2.5 group shrink-0"
        >
          <img
            src="/img/firstparty/1761063128.webp"
            alt="Beth El Center for Jewish Life"
            className="h-9 w-9 md:h-10 md:w-10 object-contain shrink-0"
          />
          <span className="font-syne font-bold tracking-tight text-sm leading-tight text-left whitespace-nowrap">
            BETH EL CENTER
            <span className="block text-[10px] font-outfit font-light tracking-[0.22em] text-gold whitespace-nowrap">FOR JEWISH LIFE · EST. 1926</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center justify-end flex-1 min-w-0 gap-1 xl:gap-3" data-testid="nav-desktop" onMouseLeave={() => setActiveGroup(null)}>
          {GROUPS.map((g) => (
            <div key={g.label} className="relative" onMouseEnter={() => setActiveGroup(g.label)}>
              <button
                data-testid={`nav-group-${slug(g.label)}`}
                onClick={() => setActiveGroup(activeGroup === g.label ? null : g.label)}
                aria-expanded={activeGroup === g.label}
                className={`flex items-center gap-1.5 px-2 xl:px-3 py-2 text-[11px] xl:text-xs tracking-[0.15em] uppercase whitespace-nowrap transition-colors duration-300 ${
                  activeGroup === g.label ? "text-gold" : "text-foreground/70 hover:text-gold"
                }`}
              >
                {g.label}
                <ChevronDown size={12} className={`transition-transform duration-300 ${activeGroup === g.label ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              <AnimatePresence>
                {activeGroup === g.label && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="absolute left-0 top-full pt-2 min-w-[220px]"
                    data-testid={`nav-dropdown-${slug(g.label)}`}
                  >
                    <div className="bg-inkdeep/95 backdrop-blur-xl border border-gold/25 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                      {g.items.map((l) =>
                        l.href.startsWith("http") ? (
                          <a
                            key={l.href}
                            href={l.href}
                            target={l.sameTab ? "_self" : "_blank"}
                            rel="noopener noreferrer"
                            data-testid={`nav-link-${slug(l.label)}`}
                            onClick={() => setActiveGroup(null)}
                            className="block px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase whitespace-nowrap text-foreground/75 hover:text-gold hover:bg-white/5 transition-colors duration-200"
                          >
                            {l.label}
                          </a>
                        ) : (
                          <button
                            key={l.href}
                            data-testid={`nav-link-${slug(l.label)}`}
                            onClick={() => go(l.href, l.sameTab)}
                            className="block w-full text-left px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase whitespace-nowrap text-foreground/75 hover:text-gold hover:bg-white/5 transition-colors duration-200"
                          >
                            {l.label}
                          </button>
                        )
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          <button
            data-testid="nav-search-events"
            onClick={() => go("/calendar#find-event")}
            aria-label="Search Events"
            title="Search Events"
            className="shrink-0 p-2 text-foreground/70 hover:text-gold transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
          >
            <Search size={15} aria-hidden="true" />
          </button>
          <button
            data-testid="nav-donate-button"
            onClick={() => { setOpen(false); window.open("https://templebethelwpb.shulcloud.com/payment.php", "_blank", "noopener"); }}
            className="ml-1 shrink-0 px-4 xl:px-5 py-2 border border-gold/60 text-gold text-[11px] tracking-[0.15em] uppercase whitespace-nowrap hover:bg-gold hover:text-inkdeep transition-colors duration-400"
          >
            Donate
          </button>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          className="lg:hidden ml-auto text-foreground p-2 shrink-0"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="lg:hidden overflow-hidden border-t border-white/10"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {GROUPS.map((g) => (
                <div key={g.label} className="mb-6">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3" data-testid={`mobile-nav-group-${slug(g.label)}`}>
                    {g.label}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {g.items.map((l) =>
                      l.href.startsWith("http") ? (
                        <a
                          key={l.href}
                          href={l.href}
                          target={l.sameTab ? "_self" : "_blank"}
                          rel="noopener noreferrer"
                          data-testid={`mobile-nav-link-${slug(l.label)}`}
                          onClick={() => setOpen(false)}
                          className="text-left font-syne text-sm uppercase tracking-wide text-foreground/80 hover:text-gold transition-colors"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <button
                          key={l.href}
                          data-testid={`mobile-nav-link-${slug(l.label)}`}
                          onClick={() => go(l.href, l.sameTab)}
                          className="text-left font-syne text-sm uppercase tracking-wide text-foreground/80 hover:text-gold transition-colors"
                        >
                          {l.label}
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
              <button
                data-testid="mobile-nav-search-events"
                onClick={() => go("/calendar#find-event")}
                className="text-left font-syne text-sm uppercase tracking-wide text-foreground/80 hover:text-gold transition-colors inline-flex items-center gap-3 mb-4"
              >
                <Search size={16} aria-hidden="true" /> Search Events
              </button>
              <div className="flex flex-col gap-3">
                <a
                  data-testid="mobile-nav-join-button"
                  href="https://templebethelwpb.shulcloud.com/form/2026-2027-temple-beth-el-membership-form"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="px-6 py-3 bg-gold text-inkdeep text-xs tracking-[0.2em] uppercase font-medium text-center"
                >
                  Join Us
                </a>
                <button
                  data-testid="mobile-nav-donate-button"
                  onClick={() => { setOpen(false); window.open("https://templebethelwpb.shulcloud.com/payment.php", "_blank", "noopener"); }}
                  className="px-6 py-3 border border-gold/60 text-gold text-xs tracking-[0.2em] uppercase text-center"
                >
                  Donate
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
